import { addMinutes, isWithinInterval } from 'date-fns';
import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';

import { EventCard } from './components/EventCard';

import { AuthPrompt } from '@/apps/Schedule/components/AuthPrompt';
import { useTeamsScheduleStore, TeamsEvent } from '@/apps/TeamsSchedule/stores/useTeamsScheduleStore';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import { useAlertStore } from '@/common/stores/useAlertStore';

export default function TeamsSchedule() {
  const { t } = useTranslation();
  const { events, loading, teamsAuthenticated, fetchEvents, checkAuthState, authenticate } = useTeamsScheduleStore();
  const { showAlert } = useAlertStore();
  const scheduleRef = useRef<HTMLDivElement>(null);

  const ALERT_CHECK_INTERVAL_MS = 10 * 1000;
  const NOTIFIED_EVENTS_KEY = 'teamsNotifiedEvents';

  const getNotifiedEvents = useCallback((): string[] => {
    const stored = localStorage.getItem(NOTIFIED_EVENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  }, []);

  const markEventAsNotified = useCallback(
    (eventId: string) => {
      const notifiedEvents = getNotifiedEvents();
      if (!notifiedEvents.includes(eventId)) {
        localStorage.setItem(NOTIFIED_EVENTS_KEY, JSON.stringify([...notifiedEvents, eventId]));
      }
    },
    [getNotifiedEvents],
  );

  const handleUpcomingMeeting = useCallback(
    (event: TeamsEvent) => {
      const isUpcoming = isWithinInterval(new Date(), {
        start: addMinutes(event.startTime, -5),
        end: event.startTime,
      });

      if (!isUpcoming) return;

      const notifiedEvents = getNotifiedEvents();
      if (notifiedEvents.includes(event.id)) return;

      showAlert({
        type: 'info',
        title: t('schedule.meetingIncoming'),
        message: t('schedule.meetingStartsIn5', { title: event.title }),
        duration: 7 * 60,
        showNotification: true,
        component: event.joinUrl ? (
          <a
            className="inline-block mt-2 px-4 py-2 bg-black text-white rounded hover:bg-zinc-700 transition-colors"
            href={event.joinUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('schedule.joinMeeting')}
          </a>
        ) : undefined
      });

      markEventAsNotified(event.id);
    },
    [showAlert, getNotifiedEvents, markEventAsNotified, t],
  );

  useEffect(() => {
    const clearNotifiedEvents = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        localStorage.removeItem(NOTIFIED_EVENTS_KEY);
      }
    };

    const interval = setInterval(clearNotifiedEvents, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useInterval(() => {
    if (teamsAuthenticated && events.length) {
      events.forEach(handleUpcomingMeeting);
    }
  }, ALERT_CHECK_INTERVAL_MS);

  useEffect(() => {
    checkAuthState();
  }, []);

  useEffect(() => {
    if (teamsAuthenticated) {
      fetchEvents();
      // Refresh events every 1 minutes
      const interval = setInterval(fetchEvents, 1 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [teamsAuthenticated]);

  const isEventPast = useCallback((event: TeamsEvent) => {
    return new Date() > event.endTime;
  }, []);

  const findCurrentOrNextEvent = useCallback(() => {
    const now = new Date();
    return events.find((event) => event.startTime >= now);
  }, [events]);

  // Scroll to current/next event
  useEffect(() => {
    if (!events.length || !scheduleRef.current) return;

    const scrollToEvent = () => {
      const currentOrNext = findCurrentOrNextEvent();
      if (!currentOrNext) return;

      const eventElement = document.getElementById(`event-${currentOrNext.id}`);
      if (eventElement) {
        eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    scrollToEvent();
    // Set up periodic scroll updates
    const interval = setInterval(scrollToEvent, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, [events, findCurrentOrNextEvent]);

  if (!teamsAuthenticated) {
    return <AuthPrompt onAuthenticate={authenticate} />;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div ref={scheduleRef} className="h-full overflow-auto relative py-2">
      <div className="max-w-2xl mx-auto">
        {events.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500 text-sm">{t('calendarApp.noMeetingsToday')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event) => (
              <div
                key={event.id}
                className={`transition-opacity duration-300 ${
                  isEventPast(event) ? 'opacity-40' : 'opacity-100'
                }`}
                id={`event-${event.id}`}
              >
                <EventCard event={event} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
